import { type ChangeEvent, type ClipboardEvent } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { extractIframeSrc } from '../lib/extract-iframe-src';
import { VideoForm } from '../model';
import { StatusSelect } from './company-access-fields';

type Props = {
  open: boolean;
  form: VideoForm;
  isSaving: boolean;
  error: string | null;
  onError: (error: string | null) => void;
  onClose: () => void;
  onSave: () => void;
  onChange: (form: VideoForm) => void;
};

const MAX_FILES = 10;
const MAX_FILE_SIZE = 100 * 1024 * 1024;
const ACCEPTED_FILES = '.pdf,.doc,.docx';

export const VideoFormDialog = ({
  open,
  form,
  isSaving,
  error,
  onError,
  onClose,
  onSave,
  onChange,
}: Props) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle>
      {form.id ? 'Редактирование видео' : 'Добавление видео'}
    </DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="Ссылка VK Video (src)"
          value={form.src}
          helperText="VK Video: «Поделиться» → «Встроить» → скопируйте «Код для вставки» (iframe) или вставьте только ссылку src"
          onPaste={(e: ClipboardEvent<HTMLInputElement>) => {
            const text = e.clipboardData?.getData('text') || '';
            const extracted = extractIframeSrc(text);
            if (!extracted) return;

            e.preventDefault();
            onChange({ ...form, src: extracted });
          }}
          onChange={(e) => {
            const value = e.target.value;
            const extracted = value.toLowerCase().includes('<iframe')
              ? extractIframeSrc(value)
              : null;
            onChange({ ...form, src: extracted || value });
          }}
          fullWidth
        />
        <TextField
          label="Название"
          value={form.name}
          onChange={(e) => onChange({ ...form, name: e.target.value })}
          fullWidth
        />
        <TextField
          label="Описание"
          value={form.description}
          onChange={(e) => onChange({ ...form, description: e.target.value })}
          fullWidth
          multiline
          minRows={3}
        />
        <Stack spacing={1}>
          <Button
            variant="outlined"
            component="label"
            disabled={isSaving || form.files.length >= MAX_FILES}
          >
            Прикрепить PDF/DOC
            <input
              hidden
              multiple
              type="file"
              accept={ACCEPTED_FILES}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const files = Array.from(event.target.files || []);
                event.target.value = '';
                if (!files.length) return;
                if (form.files.length + files.length > MAX_FILES) {
                  onError(`Можно загрузить не больше ${MAX_FILES} файлов`);
                  return;
                }
                if (files.some((file) => file.size > MAX_FILE_SIZE)) {
                  onError('Размер каждого файла не должен превышать 100 МБ');
                  return;
                }

                onError(null);
                onChange({
                  ...form,
                  files: [
                    ...form.files,
                    ...files.map((file) => ({
                      id: crypto.randomUUID(),
                      name: file.name,
                      file,
                    })),
                  ],
                });
              }}
            />
          </Button>
          {form.files.map((file) => (
            <Stack
              key={file.id}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <TextField
                label="Название файла"
                value={file.name}
                onChange={(event) =>
                  onChange({
                    ...form,
                    files: form.files.map((item) =>
                      item.id === file.id
                        ? { ...item, name: event.target.value }
                        : item,
                    ),
                  })
                }
                fullWidth
              />
              <Chip
                label={file.file?.name || file.url?.split('/').pop() || 'Файл'}
                onDelete={() =>
                  onChange({
                    ...form,
                    files: form.files.filter((item) => item.id !== file.id),
                  })
                }
              />
            </Stack>
          ))}
        </Stack>
        <StatusSelect
          value={form.status}
          onChange={(status) => onChange({ ...form, status })}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.needUserFeedback}
              onChange={(e) => {
                const needUserFeedback = e.target.checked;
                onChange({
                  ...form,
                  needUserFeedback,
                  feedbackRequiredToProgress: needUserFeedback
                    ? form.feedbackRequiredToProgress
                    : false,
                });
              }}
            />
          }
          label="Нужен ответ от пользователя"
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.feedbackRequiredToProgress}
              disabled={!form.needUserFeedback}
              onChange={(e) =>
                onChange({
                  ...form,
                  feedbackRequiredToProgress: e.target.checked,
                })
              }
            />
          }
          label="Ответ обязателен для прохождения дальше"
        />
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={isSaving}>
        Отмена
      </Button>
      <Button variant="contained" onClick={onSave} disabled={isSaving}>
        Сохранить
      </Button>
    </DialogActions>
  </Dialog>
);
